import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { ModeToggle } from '@/components/ThemeToggle';
import ThemeToggle from '@/components/ThemeToggle';
import { LanguageSelector } from '@/components/LanguageSelector';
import { motion, useScroll, useTransform, useSpring, useInView } from 'framer-motion';
import {
  ArrowRight, Shield, Lock, Menu, X,
  Scan, BarChart3, Zap, Bell,
  Hexagon, Circle, Octagon, Triangle
} from 'lucide-react';

/* ─── Rolling Digit Counter ─── */
const RollingDigit = ({ value, prefix = '', suffix = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const springVal = useSpring(0, { stiffness: 30, damping: 20 });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (isInView) springVal.set(typeof value === 'number' ? value : parseFloat(value) || 0);
  }, [isInView, value, springVal]);

  useEffect(() => {
    const unsub = springVal.on('change', v => setDisplay(Math.round(v)));
    return unsub;
  }, [springVal]);

  return (
    <span ref={ref} className="font-mono tabular-nums tracking-tight">
      {prefix}{display.toLocaleString()}{suffix}
    </span>
  );
};

/* ─── Animated Light Beam Divider ─── */
const LightBeam = () => (
  <div className="relative w-full h-px my-0 overflow-hidden">
    <div className="exec-light-beam" />
  </div>
);

/* ─── Self-drawing SVG Line Graph ─── */
const DrawGraph = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const path = 'M0,80 C30,75 60,60 90,55 C120,50 150,30 180,25 C210,20 240,35 270,15 C300,10 330,5 360,2';
  return (
    <svg ref={ref} viewBox="0 0 360 90" className="w-full h-20 mt-4" fill="none">
      <motion.path
        d={path}
        stroke="#00C853"
        strokeWidth="2"
        strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={isInView ? { pathLength: 1, opacity: 1 } : {}}
        transition={{ duration: 2, ease: 'easeInOut' }}
      />
      <motion.path
        d={path}
        stroke="#00C853"
        strokeWidth="1"
        strokeLinecap="round"
        filter="url(#glow)"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={isInView ? { pathLength: 1, opacity: 0.4 } : {}}
        transition={{ duration: 2, ease: 'easeInOut', delay: 0.3 }}
      />
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
    </svg>
  );
};

/* ─── Parallax Card Wrapper ─── */
const ParallaxCard = ({ children, speed = 0, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [60 * speed, -60 * speed]);
  const smoothY = useSpring(y, { stiffness: 100, damping: 30 });

  return (
    <motion.div
      ref={ref}
      style={{ y: smoothY }}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ─── Section Reveal Wrapper ─── */
const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ─── Abstract Geometric Icon ─── */
const GeoIcon = ({ type, className = '' }) => {
  const icons = {
    tracking: (
      <div className={`relative ${className}`}>
        <Hexagon className="w-8 h-8 text-[#3B82F6]" strokeWidth={1.2} />
        <Bell className="w-4 h-4 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      </div>
    ),
    cancel: (
      <div className={`relative ${className}`}>
        <Octagon className="w-8 h-8 text-[#EF4444]" strokeWidth={1.2} />
        <Zap className="w-4 h-4 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      </div>
    ),
    negotiate: (
      <div className={`relative ${className}`}>
        <Circle className="w-8 h-8 text-[#F59E0B]" strokeWidth={1.2} />
        <BarChart3 className="w-4 h-4 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      </div>
    ),
    insights: (
      <div className={`relative ${className}`}>
        <Triangle className="w-8 h-8 text-[#00C853]" strokeWidth={1.2} />
        <Shield className="w-4 h-4 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      </div>
    )
  };
  return icons[type] || icons.tracking;
};

/* ═══════════════════════════════════════════════ */
/*      EXECUTIVE LANDING — BRIGHT PRESTIGE        */
/* ═══════════════════════════════════════════════ */

const ExecutiveLanding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const heroRef = useRef(null);
  const { scrollYProgress: heroScroll } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroOpacity = useTransform(heroScroll, [0, 0.5], [1, 0]);
  const heroScale = useTransform(heroScroll, [0, 0.5], [1, 0.95]);

  const features = useMemo(() => [
    {
      type: 'tracking',
      tag: 'Smart Tracking',
      title: 'Every Subscription, Organized',
      desc: 'Automatic detection via email and bank statement analysis. Real-time alerts before trials convert.',
      stat: 3.2,
      statLabel: 'hidden subs found per user',
      statPrefix: '',
      statSuffix: '',
    },
    {
      type: 'cancel',
      tag: 'Instant Cancellation',
      title: 'Cancel Any Service. 4 Seconds.',
      desc: 'No settings pages. No retention calls. Our AI handles the entire process for you.',
      stat: 4,
      statLabel: 'second average cancel time',
      statPrefix: '< ',
      statSuffix: 's',
    },
    {
      type: 'negotiate',
      tag: 'Bill Negotiation',
      title: 'Lower Rates, Automatically',
      desc: 'Our AI contacts providers 24/7, finds every discount, and locks the lowest rate while you sleep.',
      stat: 20,
      statLabel: 'average bill reduction',
      statPrefix: '',
      statSuffix: '%',
    },
    {
      type: 'insights',
      tag: 'Financial Insights',
      title: 'Understand Your Spending',
      desc: 'Clear reports on where your money goes. Trends, waste analysis, and 5-year projections.',
      stat: 412,
      statLabel: 'average saved per year',
      statPrefix: '$',
      statSuffix: '',
    },
  ], []);

  return (
    <div className="exec-prestige-bg min-h-screen relative overflow-hidden" data-testid="executive-landing">
      {/* ── Ambient Background ── */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="exec-silk-gradient" />
        <div className="exec-grain-overlay" />
      </div>

      {/* ── Navigation ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 exec-nav-glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center exec-logo-glow" style={{ background: 'linear-gradient(135deg, #00C853, #00E676)' }}>
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg text-foreground tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Sub-Zero</span>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-foreground/50 hover:text-foreground transition-colors text-sm font-medium">Features</a>
              <a href="#how-it-works" className="text-foreground/50 hover:text-foreground transition-colors text-sm font-medium">How It Works</a>
              <Link to="/pricing" className="text-foreground/50 hover:text-foreground transition-colors text-sm font-medium">Pricing</Link>
              <LanguageSelector variant="compact" />
              <ModeToggle compact />
              <ThemeToggle />
              {user ? (
                <Button onClick={() => navigate('/dashboard')} className="exec-btn-primary rounded-xl font-semibold" data-testid="dashboard-nav-btn">
                  Dashboard
                </Button>
              ) : (
                <div className="flex items-center gap-3">
                  <Link to="/login">
                    <Button variant="ghost" className="text-foreground/60 hover:text-foreground hover:bg-black/5 rounded-xl font-medium" data-testid="login-nav-btn">Sign In</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="exec-btn-primary rounded-xl font-semibold" data-testid="get-started-nav-btn">Get Started</Button>
                  </Link>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 md:hidden">
              <LanguageSelector variant="compact" />
              <ModeToggle compact />
              <ThemeToggle />
              <button className="text-foreground p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-card/95 backdrop-blur-xl border-t border-border/10">
            <div className="px-4 py-4 space-y-3">
              <a href="#features" className="block text-foreground/60 hover:text-foreground py-2 font-medium">Features</a>
              <a href="#how-it-works" className="block text-foreground/60 hover:text-foreground py-2 font-medium">How It Works</a>
              <Link to="/pricing" className="block text-foreground/60 hover:text-foreground py-2 font-medium">Pricing</Link>
              {user ? (
                <Button onClick={() => navigate('/dashboard')} className="w-full exec-btn-primary rounded-xl" data-testid="dashboard-mobile-btn">Dashboard</Button>
              ) : (
                <div className="space-y-2">
                  <Link to="/login" className="block"><Button variant="outline" className="w-full rounded-xl border-black/10 text-foreground hover:bg-black/5" data-testid="login-mobile-btn">Sign In</Button></Link>
                  <Link to="/register" className="block"><Button className="w-full exec-btn-primary rounded-xl" data-testid="register-mobile-btn">Get Started</Button></Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* ═══ SCROLL 1: THE ARRIVAL ═══ */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-16 z-10">
        <motion.div style={{ opacity: heroOpacity, scale: heroScale }} className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left - Copy */}
            <div className="space-y-8 relative z-20">
              <Reveal delay={0.2}>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full exec-glass-chip">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00C853] animate-pulse" />
                  <span className="text-[#00C853] text-xs font-medium tracking-wider uppercase">Smart Subscription Management</span>
                </div>
              </Reveal>

              <Reveal delay={0.4}>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-[1.08] tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Stop the<br />Financial<br />
                  <span className="exec-text-gradient">Bleed.</span>
                </h1>
              </Reveal>

              <Reveal delay={0.6}>
                <p className="text-lg text-foreground/50 max-w-lg leading-relaxed">
                  Track, manage, and optimize all your recurring payments. Save an average of{' '}
                  <span className="text-[#00C853] font-semibold font-mono">$412</span>/year.
                </p>
              </Reveal>

              <Reveal delay={0.8}>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    size="lg"
                    onClick={() => navigate(user ? '/dashboard' : '/register')}
                    className="exec-btn-primary rounded-2xl font-semibold text-lg px-8 py-6 group"
                    data-testid="hero-cta-btn"
                  >
                    Start Saving — Free
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  <Button
                    size="lg"
                    variant="ghost"
                    onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-foreground/50 hover:text-foreground hover:bg-black/5 rounded-2xl font-medium text-lg px-8 py-6 border border-black/10"
                    data-testid="hero-features-btn"
                  >
                    See How It Works
                  </Button>
                </div>
              </Reveal>

              <Reveal delay={1.0}>
                <div className="flex flex-wrap items-center gap-6 pt-2">
                  {[
                    { icon: <Shield className="w-4 h-4 text-[#00C853]" />, text: 'Bank-grade security' },
                    { icon: <Lock className="w-4 h-4 text-[#00C853]" />, text: 'AES-256 encrypted' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(0,200,83,0.06)', border: '1px solid rgba(0,200,83,0.12)' }}>
                        {item.icon}
                      </div>
                      <span className="text-sm text-foreground/40 font-medium">{item.text}</span>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            {/* Right - Dark Hero Card (contrast element) */}
            <Reveal delay={0.5} className="relative z-20">
              <div className="exec-hero-card p-6 sm:p-8 relative overflow-hidden">
                {/* Subtle glow behind card */}
                <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full exec-orb-glow" />

                <div className="flex items-center justify-between mb-6 relative">
                  <div>
                    <p className="text-xs text-white/30 font-medium uppercase tracking-widest">Annual Savings</p>
                    <div className="text-5xl font-bold text-[#00C853] font-mono tabular-nums mt-1">
                      <RollingDigit value={412} prefix="$" />
                    </div>
                  </div>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center exec-accent-badge">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                </div>

                <div className="space-y-3 relative">
                  {[
                    { name: 'Netflix', price: '$15.49', status: 'Unused 21 days', urgency: 'high', initial: 'N' },
                    { name: 'Adobe CC', price: '$54.99', status: 'Auto-renewing', urgency: 'medium', initial: 'A' },
                    { name: 'Spotify', price: '$9.99', status: 'Active', urgency: 'low', initial: 'S' },
                  ].map((item, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.8 + i * 0.15, duration: 0.6 }}
                      className="flex items-center justify-between p-3.5 rounded-xl exec-dark-row group"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm ${
                          item.urgency === 'high' ? 'bg-red-500/80' : item.urgency === 'medium' ? 'bg-amber-500/80' : 'bg-[#00C853]/80'
                        }`}>{item.initial}</div>
                        <div>
                          <p className="font-semibold text-white text-sm">{item.name}</p>
                          <p className={`text-xs font-mono ${
                            item.urgency === 'high' ? 'text-red-400' : item.urgency === 'medium' ? 'text-amber-400' : 'text-[#00C853]'
                          }`}>{item.status}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-sm text-white">{item.price}<span className="text-white/30 text-xs">/mo</span></p>
                        {item.urgency === 'high' && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/20 font-medium">Cancel</span>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>

                <DrawGraph />
              </div>
            </Reveal>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span className="text-[10px] text-foreground/20 font-mono uppercase tracking-widest">Scroll</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="w-px h-8 bg-gradient-to-b from-[#00C853]/40 to-transparent" />
        </motion.div>
      </section>

      <LightBeam />

      {/* ═══ SCROLL 2: THE EXTRACTION (Problem) ═══ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-xs text-[#00C853] font-medium uppercase tracking-[0.2em]">The Problem</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mt-4 leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                You're Paying for Things<br />You <span className="exec-text-gradient">Don't Use</span>
              </h2>
              <p className="text-foreground/45 max-w-2xl mx-auto mt-4 text-base">
                The average person has 12 subscriptions and forgets about 3 of them. That "free trial" you forgot? It's been charging for months.
              </p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { value: 412, prefix: '$', suffix: '', label: 'wasted per year', sublabel: 'on forgotten subscriptions', color: '#E53935' },
              { value: 73, prefix: '', suffix: '%', label: 'of free trials', sublabel: 'auto-convert to paid', color: '#FB8C00' },
              { value: 4, prefix: '< ', suffix: 's', label: 'to cancel anything', sublabel: 'with Sub-Zero AI', color: '#00C853' },
            ].map((item, i) => (
              <ParallaxCard key={i} speed={0.3 + i * 0.15} delay={i * 0.15} className="exec-glass-card p-8 text-center group">
                <div className="text-5xl font-bold font-mono tabular-nums mb-3" style={{ color: item.color }}>
                  <RollingDigit value={item.value} prefix={item.prefix} suffix={item.suffix} />
                </div>
                <p className="text-sm text-foreground/70 font-medium">{item.label}</p>
                <p className="text-xs text-foreground/30 mt-1">{item.sublabel}</p>
              </ParallaxCard>
            ))}
          </div>
        </div>
      </section>

      <LightBeam />

      {/* ═══ SCROLL 3: THE COMMAND CENTER (Features) ═══ */}
      {/* ── SECOND BACKGROUND ZONE — Warm Slate ── */}
      <div className="exec-bg-zone-two relative">
      <section id="features" className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center mb-20">
              <span className="text-xs text-[#00C853] font-medium uppercase tracking-[0.2em]">Features</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mt-4 leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Four Agents. One Mission.<br /><span className="exec-text-gradient">Save Your Money.</span>
              </h2>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">
            {features.map((f, idx) => {
              const gradients = [
                'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
                'linear-gradient(135deg, #1A1A2E 0%, #2D1B3D 100%)',
                'linear-gradient(135deg, #0D1117 0%, #064E3B 100%)',
                'linear-gradient(135deg, #0C0A1D 0%, #1E1B4B 100%)',
              ];
              const iconBgs = [
                'rgba(59,130,246,0.15)',
                'rgba(239,68,68,0.15)',
                'rgba(251,191,36,0.15)',
                'rgba(0,200,83,0.15)',
              ];
              return (
              <ParallaxCard
                key={idx}
                speed={0.2 + (idx % 2) * 0.2}
                delay={idx * 0.12}
                className="rounded-3xl p-8 group transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl"
              >
                <div className="rounded-3xl p-8 h-full relative overflow-hidden" style={{ background: gradients[idx] }}>
                  {/* Subtle inner glow */}
                  <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${iconBgs[idx]} 0%, transparent 70%)` }} />
                  <div className="flex items-start justify-between mb-6 relative">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: iconBgs[idx], border: `1px solid ${iconBgs[idx]}` }}>
                      <GeoIcon type={f.type} className="brightness-200" />
                    </div>
                    <span className="text-[10px] text-white/15 font-mono uppercase tracking-widest">{String(idx + 1).padStart(2, '0')}</span>
                  </div>
                  <p className="text-[#00C853] text-xs font-medium uppercase tracking-wider mb-2">{f.tag}</p>
                  <h3 className="text-xl font-bold text-white mb-3 leading-snug" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{f.title}</h3>
                  <p className="text-sm text-white/50 leading-relaxed mb-6">{f.desc}</p>
                  <div className="pt-4 border-t border-white/8">
                    <span className="text-3xl font-bold font-mono tabular-nums text-[#00C853]">
                      <RollingDigit value={f.stat} prefix={f.statPrefix} suffix={f.statSuffix} />
                    </span>
                    <p className="text-xs text-white/25 mt-1 font-mono">{f.statLabel}</p>
                  </div>
                </div>
              </ParallaxCard>
              );
            })}
          </div>
        </div>
      </section>

      <LightBeam />

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="how-it-works" className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-20">
              <span className="text-xs text-[#00C853] font-medium uppercase tracking-[0.2em]">How It Works</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mt-4 leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                3 Steps to<br /><span className="exec-text-gradient">Start Saving</span>
              </h2>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-3 gap-8">
            {[
              { step: '01', icon: <Scan className="w-6 h-6 text-[#3B82F6]" />, title: 'Connect', desc: 'Link your email or bank account. We find every subscription automatically.', gradient: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)', accent: 'rgba(59,130,246,0.15)' },
              { step: '02', icon: <BarChart3 className="w-6 h-6 text-[#F59E0B]" />, title: 'Review', desc: 'See all subscriptions in one place. We highlight ones you can save on.', gradient: 'linear-gradient(135deg, #1C1917 0%, #292524 100%)', accent: 'rgba(245,158,11,0.15)' },
              { step: '03', icon: <Zap className="w-6 h-6 text-[#00C853]" />, title: 'Save', desc: 'Cancel unused services, negotiate lower rates, and watch savings grow.', gradient: 'linear-gradient(135deg, #052E16 0%, #14532D 100%)', accent: 'rgba(0,200,83,0.15)' },
            ].map((item, idx) => (
              <ParallaxCard key={idx} speed={0.15} delay={idx * 0.15} className="text-center">
                <div className="rounded-3xl p-8 h-full relative overflow-hidden" style={{ background: item.gradient }}>
                  <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-30" style={{ background: `radial-gradient(circle, ${item.accent} 0%, transparent 70%)` }} />
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-6 relative" style={{ background: item.accent }}>
                    {item.icon}
                  </div>
                  <span className="text-xs text-[#00C853]/50 font-mono">{item.step}</span>
                  <h3 className="text-xl font-bold text-white mt-2 mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{item.title}</h3>
                  <p className="text-sm text-white/50 leading-relaxed">{item.desc}</p>
                </div>
              </ParallaxCard>
            ))}
          </div>
        </div>
      </section>

      <LightBeam />

      {/* ═══ SECURITY / TRUST ═══ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="exec-glass-card p-8 sm:p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-20 bg-gradient-to-b from-[#00C853]/30 to-transparent" />
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(0,200,83,0.06)', border: '1px solid rgba(0,200,83,0.12)' }}>
                <Shield className="w-8 h-8 text-[#00C853]" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Your Data is <span className="exec-text-gradient">Protected</span>
              </h2>
              <p className="text-foreground/45 max-w-xl mx-auto mb-8">
                AES-256 encryption at rest. Sandboxed AI agents. Zero data sale policy. We treat your financial data with the security it deserves.
              </p>
              <div className="grid sm:grid-cols-3 gap-6">
                {[
                  { value: '256', label: 'bit encryption', sub: 'AES-GCM standard' },
                  { value: '19', label: 'currencies', sub: 'Global coverage' },
                  { value: '0', label: 'data sold', sub: 'Ever. Period.' },
                ].map((item, i) => (
                  <div key={i} className="p-4">
                    <p className="text-3xl font-bold font-mono text-[#00C853]">{item.value}</p>
                    <p className="text-sm text-foreground/60 font-medium mt-1">{item.label}</p>
                    <p className="text-xs text-foreground/30 mt-0.5">{item.sub}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Ready to Stop<br /><span className="exec-text-gradient">Wasting Money?</span>
            </h2>
            <p className="text-lg text-foreground/45 mb-10">
              Join thousands who save an average of $412/year with Sub-Zero.
            </p>
            <Button
              size="lg"
              onClick={() => navigate(user ? '/dashboard' : '/register')}
              className="exec-btn-primary rounded-2xl font-semibold text-lg px-10 py-7 group"
              data-testid="final-cta-btn"
            >
              Get Started — It's Free
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Reveal>
        </div>
      </section>
      </div>{/* End of exec-bg-zone-two */}

      {/* ═══ Footer ═══ */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-black/5 relative z-10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #00C853, #00E676)' }}>
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm text-foreground/40 font-medium">Sub-Zero</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-foreground/30">
            <Link to="/privacy" className="hover:text-foreground/60 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground/60 transition-colors">Terms</Link>
            <Link to="/faq" className="hover:text-foreground/60 transition-colors">FAQ</Link>
            <Link to="/contact" className="hover:text-foreground/60 transition-colors">Contact</Link>
          </div>
          <p className="text-xs text-foreground/20">&copy; 2026 THOQ Ltd. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ExecutiveLanding;
